"use client";

import { useEffect, useRef, useState } from "react";
import { Capacitor } from "@capacitor/core";
import { signIn } from "next-auth/react";

// Use current origin so it works on any Vercel deployment (prod, preview, dev)
const SERVER_URL = typeof window !== "undefined" ? window.location.origin : "https://cacique-agro.vercel.app";

/**
 * On native Capacitor (iOS/Android), intercepts OAuth sign-in to use
 * SFSafariViewController (via @capacitor/browser) instead of opening Safari.
 *
 * On web, renders nothing — the server-action forms handle OAuth normally.
 */
export default function NativeOAuthButtons({ appleEnabled = false }: { appleEnabled?: boolean }) {
  const [isNative, setIsNative] = useState(false);
  const [loading, setLoading] = useState<"google" | "apple" | null>(null);
  const listenerRef = useRef(false);

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    setIsNative(true);

    // Listen for the URL scheme callback from SFSafariViewController
    if (listenerRef.current) return;
    listenerRef.current = true;

    import("@capacitor/app").then(({ App }) => {
      App.addListener("appUrlOpen", async ({ url }) => {
        if (!url.startsWith("caciqueagro://auth/callback")) return;

        // Close the in-app browser
        try {
          const { Browser } = await import("@capacitor/browser");
          await Browser.close();
        } catch {}

        // Extract the token from the URL
        const tokenMatch = url.match(/[?&]token=([^&]+)/);
        if (!tokenMatch) {
          setLoading(null);
          return;
        }

        const token = decodeURIComponent(tokenMatch[1]);

        // Exchange the token for a real NextAuth session in the WKWebView
        const result = await signIn("native-auth", {
          token,
          redirect: false,
        });

        if (result?.ok) {
          window.location.href = "/";
        } else {
          setLoading(null);
        }
      });
    });

    // Reset loading state if user closes the in-app browser without completing
    import("@capacitor/browser").then(({ Browser }) => {
      Browser.addListener("browserFinished", () => {
        setLoading(null);
      });
    });
  }, []);

  if (!isNative) return null;

  const handleOAuth = async (provider: "google" | "apple") => {
    setLoading(provider);
    try {
      const { Browser } = await import("@capacitor/browser");
      const callbackUrl = encodeURIComponent("/auth/native-callback");
      await Browser.open({
        url: `${SERVER_URL}/auth/native-signin/${provider}?callbackUrl=${callbackUrl}`,
        presentationStyle: "fullscreen",
      });
    } catch {
      setLoading(null);
    }
  };

  return (
    <div className="space-y-3" data-native-oauth>
      <button
        type="button"
        onClick={() => handleOAuth("google")}
        disabled={loading !== null}
        className="flex w-full items-center justify-center gap-3 rounded-xl border border-border bg-surface-hover px-4 py-3 text-sm font-medium text-text transition hover:bg-surface-elevated disabled:opacity-50"
      >
        <svg className="h-5 w-5" viewBox="0 0 24 24">
          <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4" />
          <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
          <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
          <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
        </svg>
        {loading === "google" ? "Abriendo..." : "Continuar con Google"}
      </button>

      {appleEnabled && (
        <button
          type="button"
          onClick={() => handleOAuth("apple")}
          disabled={loading !== null}
          className="flex w-full items-center justify-center gap-3 rounded-xl bg-white px-4 py-3 text-sm font-medium text-black transition hover:bg-gray-100 disabled:opacity-50"
        >
          <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12.152 6.896c-.948 0-2.415-1.078-3.96-1.04-2.04.027-3.91 1.183-4.961 3.014-2.117 3.675-.54 9.103 1.519 12.09 1.013 1.459 2.208 3.09 3.792 3.029 1.52-.065 2.09-.987 3.925-.987 1.831 0 2.35.987 3.96.948 1.637-.026 2.676-1.48 3.676-2.948 1.156-1.688 1.636-3.325 1.662-3.415-.039-.013-3.182-1.221-3.22-4.857-.026-3.04 2.48-4.494 2.597-4.559-1.429-2.09-3.623-2.324-4.39-2.376-2-.156-3.675 1.09-4.6 1.09zm3.378-3.066c.843-1.012 1.4-2.427 1.245-3.83-1.207.052-2.662.805-3.532 1.818-.78.896-1.454 2.338-1.273 3.714 1.338.104 2.715-.688 3.56-1.702z" />
          </svg>
          {loading === "apple" ? "Abriendo..." : "Continuar con Apple"}
        </button>
      )}
    </div>
  );
}
